import { useState } from 'react'
import type { Navigate } from '../App'
import { useActiveIslands, useStore } from '../state/store'
import { languageOf } from '../lib/languages'
import { readiness } from '../lib/analysis'
import { dueLabel } from '../lib/srs'
import { freshSrs } from '../lib/srs'
import { uid } from '../lib/id'
import { levelFor, levelName, scaleStep, splitByLevel } from '../lib/levels'
import type { Island } from '../types'

export function IslandsView({ navigate }: { navigate: Navigate }) {
  // Collapsed on every visit on purpose: the point is that the level you
  // are on decides what the screen shows.
  const [showAhead, setShowAhead] = useState(false)
  const { data } = useStore()
  const islands = useActiveIslands()
  const lang = languageOf(data.settings.activeLanguage)

  /*
   * Two lists, not one.
   *
   * The five identity islands are the head of the starter set and, at HSK 3,
   * also the hardest thing in it — so the first screen a beginner ever saw was
   * the content they were least able to use. Splitting on the level they told
   * us puts what they can drill today at the top and everything else below a
   * line, in the order it was already in. Nothing is hidden and nothing is
   * locked; the harder islands open and drill exactly as before.
   */
  const level = levelFor(data.settings, data.settings.activeLanguage)
  const { ready, ahead, outgrown } = splitByLevel(islands, level)

  /*
   * Hands the editor a draft rather than writing one.
   *
   * This used to dispatch upsertIsland first and navigate second, which made
   * "Cancel" in the editor a lie: the island already existed, so cancelling
   * left a blank "New island" with one empty block in the list and landed you
   * on its empty detail page. Nothing is written now until Save.
   */
  const addIsland = () => {
    const now = Date.now()
    const id = uid('isl')
    navigate({
      view: 'edit',
      id,
      draft: {
        id,
        language: data.settings.activeLanguage,
        title: 'New island',
        titleZh: lang.ui.newIsland,
        useWhen: '',
        tags: [],
        // Where they are, not a constant. The editor's own dropdown still lets
        // them say otherwise before saving.
        hskLevel: level,
        blocks: [{ id: uid('blk'), hanzi: '', pinyin: '', english: '' }],
        srs: freshSrs(),
        stats: { performances: 0, bestScore: 0, lastScore: null, lastDrilledAt: null },
        isStarter: false,
        createdAt: now,
        updatedAt: now,
      },
    })
  }

  const renderList = (list: Island[]) => (
    <div className="stack">
      {list.map((island) => {
        const state = readiness(island)
        return (
          <div key={island.id} className="card">
            <div className="card-head">
              <div>
                <div className="hanzi" style={{ fontSize: '1.35rem' }}>
                  {island.titleZh}
                </div>
                <div className="muted" style={{ fontSize: '0.9rem' }}>
                  {island.title}
                </div>
              </div>
              <div className="row">
                <span className={`badge is-${state}`}>{state}</span>
                <span className="badge">
                  {levelName(lang.code)} {island.hskLevel}
                </span>
              </div>
            </div>

            {island.useWhen && (
              <p className="english" style={{ margin: '0 0 0.7rem' }}>
                {island.useWhen}
              </p>
            )}

            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="data muted" style={{ fontSize: '0.76rem' }}>
                {island.blocks.length} blocks · {dueLabel(island.srs)} ·{' '}
                {island.stats.performances} runs
              </span>
              <span className="row">
                <button
                  type="button"
                  className="btn is-small"
                  onClick={() => navigate({ view: 'island', id: island.id })}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="btn is-small is-primary"
                  onClick={() => navigate({ view: 'recall', id: island.id })}
                >
                  Drill
                </button>
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Your islands</div>
        <h1 className="page-title">
          <span className="page-title-zh">{lang.ui.islands}</span>
        </h1>
        <p className="lede">
          Each island is a topic you can hold up on your own for thirty seconds. Build few, drill
          them past the point where you have to think.
        </p>
      </header>

      <div className="row" style={{ marginBottom: '1.1rem' }}>
        <button type="button" className="btn is-primary" onClick={addIsland}>
          Build an island
        </button>
        {/* Beside the empty-island button rather than behind a menu: the moment
            you are least likely to want a blank form is the moment you have
            just been handed one. */}
        <button type="button" className="btn" onClick={() => navigate({ view: 'create' })}>
          Build one with AI
        </button>
      </div>

      {islands.length === 0 ? (
        <div className="empty">
          Nothing here yet. Build an island, or restore the starter set from Settings.
        </div>
      ) : (
        <>
          {ready.length > 0 ? (
            renderList(ready)
          ) : (
            <div className="empty">
              Nothing at {scaleStep(lang.code, level)} yet — everything you have is pitched
              higher. Move the level in the rail, or open one anyway.
            </div>
          )}

          {outgrown.length > 0 && (
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0.9rem 0 0' }}>
              {outgrown.length} beginner {outgrown.length === 1 ? 'version' : 'versions'} of islands
              you already have — “{outgrown[0].title}” and the rest — are set aside at this level.
              Drop the level in the rail to bring them back.
            </p>
          )}

          {ahead.length > 0 && (
            <>
              <div className="divider" style={{ marginTop: '1.6rem' }} />
              {/*
                Collapsed, not merely pushed down.
                
                They used to be listed inline under a heading, which meant the
                level control changed almost nothing you could see: the same
                islands in the same order, with a small label sliding up and
                down. Raising your level has to reveal something, or the
                control does not read as doing anything at all.

                Still one press away, and the count is on the button, so
                nothing is hidden in the sense of being lost.
              */}
              <button
                type="button"
                className="btn is-small is-quiet"
                aria-expanded={showAhead}
                onClick={() => setShowAhead((v) => !v)}
                style={{ marginTop: '1.1rem' }}
              >
                {showAhead ? 'Hide' : 'Show'} {ahead.length}{' '}
                {ahead.length === 1 ? 'island' : 'islands'} above {scaleStep(lang.code, level)}
              </button>

              {showAhead && (
                <>
                  <p className="muted" style={{ fontSize: '0.86rem', margin: '0.9rem 0 1rem' }}>
                    Pitched past where you said you are — open and drill any of them, or move your
                    level in the rail and they join the list above.
                  </p>
                  {renderList(ahead)}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
